import { useCallback, useEffect } from 'react';
import { useExecuteGameWinMutation } from '../modules/game/services/GameService';
import { TGameType } from '../modules/game/helpers';

const PENDING_WIN_EVENTS_KEY = 'pendingWinEvents';

type PendingWinEvent = {
  userId: string | null;
  gameCode: TGameType;
  eventId: string;
};

type UseWinEventParams = {
  gameCode: TGameType;
  eventId: string;
  userId: string | null;
  maxAttempts?: number;
  retryDelayMs?: number;
  requestTimeoutMs?: number;
};

function readPendingEvents(): PendingWinEvent[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(PENDING_WIN_EVENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePendingEvents(events: PendingWinEvent[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PENDING_WIN_EVENTS_KEY, JSON.stringify(events));
  } catch {
    // ничего не показываем пользователю
  }
}

function addPendingWinEventToStorage(event: PendingWinEvent) {
  const prev = readPendingEvents();
  const next = [...prev, event];
  writePendingEvents(next);
}

async function sendWithRetry<T>(fn: () => Promise<T>, maxAttempts = 5, delayMs = 1000): Promise<T> {
  let attempt = 0;
  let lastError: unknown;

  while (attempt < maxAttempts) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      attempt += 1;

      if (attempt >= maxAttempts) {
        throw lastError;
      }

      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  throw lastError as T;
}

async function callWithTimeout<TArg, TResult>(trigger: (arg: TArg) => any, arg: TArg, timeoutMs: number): Promise<TResult> {
  const mutationPromise = trigger(arg);
  const abortable = mutationPromise as typeof mutationPromise & {
    abort?: () => void;
    unwrap: () => Promise<TResult>;
  };

  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      abortable.abort?.();
      reject(new Error('timeout'));
    }, timeoutMs);
  });

  try {
    return (await Promise.race([abortable.unwrap(), timeoutPromise])) as TResult;
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

export function useWinEvent({
  gameCode,
  eventId,
  userId,
  maxAttempts = 5,
  retryDelayMs = 1000,
  requestTimeoutMs = 5000,
}: UseWinEventParams) {
  const [executeGameWinEvent] = useExecuteGameWinMutation();

  const sendWinEvent = useCallback(async (): Promise<boolean> => {
    const payload = { gameCode, eventId };

    // если явный оффлайн — просто копим
    if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
      addPendingWinEventToStorage({ userId, ...payload });
      return false;
    }

    try {
      const attempt = () => callWithTimeout<typeof payload, unknown>(executeGameWinEvent, payload, requestTimeoutMs);

      await sendWithRetry(attempt, maxAttempts, retryDelayMs);
      return true;
    } catch (err) {
      console.error('Failed to execute win event (error or timeout), saving to localStorage', err);
      addPendingWinEventToStorage({ userId, ...payload });
      return false;
    }
  }, [executeGameWinEvent, gameCode, eventId, userId, maxAttempts, retryDelayMs, requestTimeoutMs]);

  const flushPending = useCallback(async () => {
    if (!userId) return;
    if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
      return;
    }

    const events = readPendingEvents();
    if (!events.length) return;

    const remaining: PendingWinEvent[] = [];

    for (const e of events) {
      if (e.userId !== userId || e.gameCode !== gameCode) {
        // не трогаем чужие
        remaining.push(e);
        continue;
      }

      try {
        await executeGameWinEvent({
          gameCode: e.gameCode,
          eventId: e.eventId,
        }).unwrap();
      } catch (err) {
        console.error('Failed to resend stored win event', err);
        remaining.push(e);
      }
    }

    writePendingEvents(remaining);
  }, [executeGameWinEvent, gameCode, userId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // один раз при маунте, если уже онлайн
    if (navigator.onLine) {
      void flushPending();
    }

    const handleOnline = () => {
      void flushPending();
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [flushPending]);

  return { sendWinEvent };
}
